import { DragDropModule } from '@angular/cdk/drag-drop';
import { ConnectedPosition, OverlayModule, ScrollStrategyOptions } from '@angular/cdk/overlay';
import {
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { DocumentItemTypeEnum, DocumentTypeEnum, UserController } from '@domain';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    RaLoadingComponent,
    RaMediaTypeAvatarComponent,
    RaUserAvatarComponent,
} from '@shared/components';
import { RaModalEditTextComponent } from 'src/app/shared/components/ra-modal-edit-text/ra-modal-edit-text.component';
import { DocumentSessionPartial } from 'src/app/domain/entities/partials/documents/document-session.partial';
import { RaTooltipDirective } from 'src/app/shared/directives';
import { ChecklistStep } from 'src/app/shared/pipes/documents/checklist-completion.pipe';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { getAvatarColorForIndex } from 'src/app/shared/utils/avatar-colors';
import { deltaToPlainText } from 'src/app/shared/utils/delta.util';
import { toHeadingLines } from '../../../../../pdf-renderer/quill-delta-converter';
import { RaEditTooltipDirective } from '../../../directives/ra-edit-tooltip/ra-edit-tooltip.directive';
import { EditDataService } from '../../../services/_edit-data.service';
import { EditSectionService } from '../../../services/edit-section.service';
import { EditService } from '../../../services/edit-service';
import { SectionOptionsComponent } from './section-options/section-options.component';

/** Entrada do sumário da seção: um item TITLE/SUBTITLE ou uma linha título/subtítulo de um texto. */
interface OutlineEntry {
    key: string;
    _id: string;
    type: DocumentItemTypeEnum;
    label: string;
    /** Posição da linha entre os títulos/subtítulos do texto (só nas linhas). */
    lineIndex?: number;
}

@Component({
    selector: 'section-card',
    templateUrl: 'section-card.component.html',
    styleUrl: 'section-card.component.scss',
    imports: [
        DragDropModule,
        OverlayModule,
        RaEditTooltipDirective,
        RaMediaTypeAvatarComponent,
        RaTooltipDirective,
        SectionOptionsComponent,
        RaLoadingComponent,
        RaUserAvatarComponent,
        TranslatePipe,
        RaIconComponent,
        RaModalEditTextComponent,
        DatePipe,
    ],
})
export class SectionCardComponent {
    /**
     * Menu à direita da coluna (fora do overflow da lista), com reposição ao scroll.
     * A 1ª posição abre para baixo; quando não há espaço (ex.: último card no rodapé),
     * o CDK usa a 2ª posição e abre para cima, evitando que o menu fique cortado.
     */
    public readonly sessionOptionsPositions: ConnectedPosition[] = [
        {
            originX: 'end',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'top',
            offsetX: 26,
            offsetY: -24,
        },
        {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'bottom',
            offsetX: 26,
            offsetY: 24,
        },
    ];

    public readonly scrollStrategy = inject(ScrollStrategyOptions).reposition();

    public section = input.required<DocumentSessionPartial>();
    public disabled = input(false);
    public collapsed = input(false);
    /** Etapa desta seção na trilha do checklist (null fora do modo checklist / assinatura). */
    public step = input<ChecklistStep | null>(null);
    public isHiddenOptions = signal(true);
    public isEditingName = signal(false);
    public editingName = signal('');
    public loading = signal(false);

    // * [Checklist]
    public isEditingDescription = signal(false);

    private nameInputRef = viewChild<ElementRef<HTMLInputElement>>('nameInput');

    // * [Injects]
    public editService = inject(EditService);
    public userController = inject(UserController);
    public editDataService = inject(EditDataService);
    private _editSessionService = inject(EditSectionService);
    private _translate = inject(TranslateService);

    /**
     * Token base da cor do tipo do documento (accentBlue | accentGreen | accentOrange),
     * mesma convenção da pipe `iconColorByDocumentType` e dos cards de criação.
     * Alimenta o destaque do card ativo via CSS vars --session-accent[-light].
     */
    public accentToken = computed(() => {
        switch (this.editDataService.document()?.type) {
            case DocumentTypeEnum.QUOTE:
                return 'accentOrange';
            case DocumentTypeEnum.CONTRACT:
                return 'accentGreen';
            default:
                return 'accentBlue';
        }
    });

    /**
     * Abre (navega para) a seção. Documento enviado para assinatura
     * (PUBLISHED/SIGNED) fica congelado: `disabled` bloqueia a navegação, igual
     * ao card esmaecido — evita entrar numa seção que não pode ser editada.
     */
    public openSection(): void {
        if (this.disabled()) return;
        // Na aba do orçamento a lista está travada: abrir uma seção daqui levaria
        // o usuário a editar o CONTEÚDO achando que mexe no orçamento.
        if (this.editDataService.sessionsLocked()) return;
        this.editDataService.setCurrentSession(this.section());
    }

    /** Tooltip para avatar de foco na seção. */
    public sessionFocusTooltip(): string {
        const focused = this.section().focused!;
        const userId = this.userController.getCurrentUserId();
        const name =
            focused.userId === userId
                ? this._translate.instant('document.edit.sessionCard.you')
                : focused.userName;
        return this._translate.instant('document.edit.sessionCard.focusedOnSession', { name });
    }

    constructor() {
        // Quando entrar em modo de edição, inicializa o valor e dá focus no input.
        // `section()` é lido com untracked para que atualizações vindas do pusher
        // (ex.: refreshFocusInSection) NÃO re-disparem o effect e sobrescrevam o
        // que o usuário está digitando, revertendo para o nome antigo da seção.
        effect(() => {
            if (this.isEditingName()) {
                untracked(() => {
                    this.editingName.set(this.section().name);
                    setTimeout(() => this.nameInputRef()?.nativeElement?.focus(), 0);
                });
            }
        });
    }

    public onOptionsClickOutside(): void {
        if (!this.isHiddenOptions()) {
            this.isHiddenOptions.set(true);
        }
    }

    public typoAllTextsIa(): void {
        // Delega ao EditService para reusar o overlay full-screen bloqueante
        // (impede sair da tela durante a correção — evita o "erro de servidor").
        this.editService.typoAllTextsIa(this.section()?._id);
    }

    public saveName(): void {
        if (this.loading()) return;

        // Nome vazio ou inalterado: apenas fecha a edição, sem chamar a API
        const newName = this.editingName().trim();
        if (!newName || newName === this.section()?.name) {
            this.cancelEdit();
            return;
        }

        this.loading.set(true);
        this.isEditingName.set(false);
        this._editSessionService.changeNameSession(this.section()?._id, newName).subscribe({
            next: () => {
                this.loading.set(false);
                this.isHiddenOptions.set(true);
            },
            error: () => {
                this.loading.set(false);
                this.cancelEdit();
            },
        });
    }

    public cancelEdit(): void {
        this.isEditingName.set(false);
        this.editingName.set('');
        this.isHiddenOptions.set(true);
    }

    // * [Checklist]

    /** `true` quando o documento está em modo checklist. */
    public isChecklistMode = computed(
        () => this.editDataService.document()?.config?.checklistMode ?? false
    );

    /** `true` quando a seção está concluída. */
    public isDone = computed(() => this.section()?.checklist?.done ?? false);

    /** `true` quando a seção já tem uma descrição preenchida. */
    public hasDescription = computed(() => !!(this.section()?.description ?? '').trim());

    /** Seção com pastas de checklist: o check é DERIVADO (conclui sozinho quando
     *  todas as pastas estão concluídas) → checkbox desabilitado. */
    public hasFolders = computed(() =>
        (this.section()?.items ?? []).some(
            item => item.type === DocumentItemTypeEnum.CHECKLIST_FOLDER
        )
    );

    /** Responsável/criador/owner-admin podem marcar; não em doc bloqueado nem em
     *  seção derivada por pastas. */
    public canComplete = computed(
        () =>
            this.editDataService.canCompleteChecklist() &&
            !this.disabled() &&
            !this.hasFolders()
    );

    /** Marca/desmarca a seção (delegado ao EditService, com update otimista). */
    public onToggleDone(done: boolean): void {
        if (!this.canComplete()) return;
        this.editService.markSectionCompleted(this.section()._id, done);
    }

    // * [Trilha de etapas]

    /** Lista aberta em modo checklist e a seção é uma etapa: desenha nó + trilha. */
    public showRail = computed(() => this.isChecklistMode() && !this.collapsed() && !!this.step());

    /** Próxima etapa pendente: nó com halo azul e selo "Próxima". */
    public isNextStep = computed(() => !!this.step()?.current && !this.isDone());

    /** O nó é o check: alterna a conclusão. */
    public toggleStep(): void {
        this.onToggleDone(!this.isDone());
    }

    /** Tooltip do nó: marcar/desmarcar, ou por que não pode. */
    public stepTooltip(): string {
        if (!this.canComplete()) {
            return this._translate.instant('document.edit.checklist.onlyResponsibles');
        }
        return this._translate.instant(
            this.isDone() ? 'document.edit.checklist.unmarkStep' : 'document.edit.checklist.markStep'
        );
    }

    /** "Concluída por X · 15:09" se foi hoje; senão a data. */
    public doneAtFormat = computed(() => {
        const at = this.section()?.checklist?.doneAt;
        if (!at) return null;
        const sameDay = new Date(at).toDateString() === new Date().toDateString();
        return sameDay ? 'shortTime' : 'shortDate';
    });

    /** Abre o modal da descrição da seção (ver/adicionar/editar). */
    public openDescription(): void {
        this.isEditingDescription.set(true);
    }

    /** Salva a descrição (se mudou) e fecha o modal. */
    public saveDescription(value: string): void {
        this.isEditingDescription.set(false);
        if (this.disabled()) return;
        const description = (value ?? '').trim();
        if (description === (this.section()?.description ?? '')) return;
        this.editService.updateSessionDescription(this.section()._id, description);
    }

    // * [Sumário da seção ativa]

    /** Enum exposto para o template do sumário. */
    public readonly itemTypes = DocumentItemTypeEnum;

    /**
     * Títulos/subtítulos da seção ativa, em texto plano, para navegar direto
     * ao bloco. Vazio fora da seção ativa e no rail (mesmo layout com e sem
     * modo checklist).
     *
     * Entram os itens TITLE/SUBTITLE e as LINHAS marcadas como título/subtítulo dentro dos
     * textos (formato `header` do Delta, a classificação por linha do editor), na ordem da
     * folha. É a mesma leitura do sumário do PDF (`toHeadingLines`); sem as linhas, um título
     * escrito pela barra do topo não aparecia aqui.
     */
    public sectionOutline = computed<OutlineEntry[]>(() => {
        if (this.collapsed()) return [];
        if (this.editDataService.currentSession()?._id !== this.section()?._id) return [];

        const outline: OutlineEntry[] = [];
        for (const item of this.section()?.items ?? []) {
            if (item.type === DocumentItemTypeEnum.TITLE || item.type === DocumentItemTypeEnum.SUBTITLE) {
                outline.push({
                    key: item._id,
                    _id: item._id,
                    type: item.type,
                    label: deltaToPlainText(item.value) || '…',
                });
                continue;
            }

            if (item.type !== DocumentItemTypeEnum.TEXT) continue;
            const value = typeof item.value === 'string' ? item.value : JSON.stringify(item.value ?? '');
            toHeadingLines(value).forEach((line, index) => {
                // Linha de título ainda vazia (acabou de apertar a classe) não entra; o índice
                // conta mesmo assim, porque é a posição dela entre os h1/h2 do card.
                if ((line.level !== 1 && line.level !== 2) || !line.text.trim()) return;
                outline.push({
                    key: `${item._id}:${index}`,
                    _id: item._id,
                    type: line.level === 1 ? DocumentItemTypeEnum.TITLE : DocumentItemTypeEnum.SUBTITLE,
                    label: line.text.trim(),
                    lineIndex: index,
                });
            });
        }
        return outline;
    });

    /**
     * Rola a folha até o bloco do título clicado no sumário. Título que é uma linha dentro
     * de um texto rola até a própria linha, não até o topo do card.
     *
     * Move só o painel de itens, na mão. `scrollIntoView` rolaria TODOS os
     * ancestrais roláveis, e o `.container-content` do editor é overflow:hidden:
     * o que ele andasse não teria barra para voltar — a tela subia e ficava lá.
     */
    public goToItem(fieldId: string, lineIndex?: number): void {
        const card = document.querySelector<HTMLElement>(`[data-field-id="${fieldId}"]`);
        const scroller = card?.closest<HTMLElement>('section-items');
        if (!card || !scroller) return;

        const line =
            lineIndex === undefined
                ? null
                : (card.querySelectorAll<HTMLElement>('h1, h2')[lineIndex] ?? null);
        const target = line ?? card;

        // O padding-topo do painel é a folga do header flutuante — o mesmo valor
        // do `scroll-padding-top` que o `scrollIntoView` respeitava.
        const clearance = parseFloat(getComputedStyle(scroller).paddingTop) || 0;
        const offset =
            scroller.scrollTop +
            target.getBoundingClientRect().top -
            scroller.getBoundingClientRect().top -
            clearance -
            (line ? 16 : 0);

        scroller.scrollTo({
            top: Math.max(0, Math.min(offset, scroller.scrollHeight - scroller.clientHeight)),
            behavior: 'smooth',
        });
    }

    /** Cor do usuário que está com foco na seção (mesma lógica do header). */
    public getFocusedUserColor(): string {
        const focused = this.section()?.focused;
        if (!focused?.userId) return getAvatarColorForIndex(0);

        const onlineUsers = this.editDataService.document()?.onlineUsers ?? [];
        const userIndex = onlineUsers.findIndex(u => u._id === focused.userId);
        return getAvatarColorForIndex(userIndex);
    }
}
